import { useState, useCallback } from 'react';

interface ModalState {
  isOpen: boolean;
  title: string;
  message: string;
  type: 'info' | 'success' | 'warning' | 'error';
  confirmText?: string;
  cancelText?: string;
  onConfirm?: () => void;
  showCancel?: boolean;
}

const initialModalState: ModalState = {
  isOpen: false,
  title: '',
  message: '',
  type: 'info',
  confirmText: 'OK',
  cancelText: 'Cancel',
  showCancel: false,
};

export const useModal = () => {
  const [modalState, setModalState] = useState<ModalState>(initialModalState);

  const showModal = useCallback((options: Partial<ModalState> & { title: string; message: string }) => {
    setModalState({
      ...initialModalState,
      ...options,
      isOpen: true,
    });
  }, []);

  const hideModal = useCallback(() => {
    setModalState(prev => ({ ...prev, isOpen: false }));
  }, []);

  // Convenience methods for different types of modals
  const showInfo = useCallback((title: string, message: string, onConfirm?: () => void) => {
    showModal({ title, message, type: 'info', onConfirm });
  }, [showModal]);

  const showSuccess = useCallback((title: string, message: string, onConfirm?: () => void) => {
    showModal({ title, message, type: 'success', onConfirm });
  }, [showModal]);

  const showWarning = useCallback((title: string, message: string, onConfirm?: () => void) => {
    showModal({ title, message, type: 'warning', onConfirm });
  }, [showModal]);

  const showError = useCallback((title: string, message: string, onConfirm?: () => void) => {
    showModal({ title, message, type: 'error', onConfirm });
  }, [showModal]);

  const showConfirm = useCallback((
    title: string, 
    message: string, 
    onConfirm: () => void,
    confirmText = 'Confirm',
    cancelText = 'Cancel'
  ) => {
    showModal({ 
      title, 
      message, 
      type: 'warning', 
      onConfirm, 
      showCancel: true,
      confirmText,
      cancelText
    });
  }, [showModal]);

  return {
    modalState,
    showModal,
    hideModal,
    showInfo,
    showSuccess,
    showWarning,
    showError,
    showConfirm,
  };
};